import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Center,
  Button,
  Badge,
} from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useOpenRestaurantProfile } from '@/hooks/useOpenRestaurantProfile';
import { useSelector } from 'react-redux';
import { AppHeader } from '@/components';
import { RestaurantItemCard } from '@/pages/lists/components/shared/RestaurantItemCard';
import { DistanceDisplay } from '@/pages/lists/components/shared/DistanceDisplay';
import { useGetHistory } from '@/api';
import { selectPosition } from '@/store/slices/locationSlice';
import { Restaurant } from '@/types/restaurant';

interface HistoryRestaurant extends Restaurant {
  /** API returns snake_case, but the apiClient transforms it to camelCase on response */
  ratedAt: string;
  distance: number;
  distanceDisplay: string;
}

const HistoryItem = ({ restaurant, userLocation }: { restaurant: HistoryRestaurant; userLocation: any }) => {
  const openRestaurantProfile = useOpenRestaurantProfile();

  // Warm chunk only. History is a full summary shape but the server caches it
  // and refreshes it on rating events only, so `isBookmarked`/`notes` can be
  // stale: not seeded, the profile takes the cold path for fresh data.
  const handleClick = () => {
    openRestaurantProfile(restaurant.id);
  };

  const formatRatingDate = (dateString: string) => {
    // Ensure the timestamp is interpreted as UTC. If the API returns a naive
    // datetime string (no 'Z' or offset), JavaScript would parse it as local
    // time which shifts the value by the user's UTC offset. Appending 'Z'
    // forces UTC interpretation, matching the server's storage timezone.
    const utcString = dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString)
      ? dateString
      : `${dateString}Z`;
    const date = new Date(utcString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  return (
    <Box
      cursor="pointer"
      onClick={handleClick}
      _hover={{ bg: "var(--card-surface-2)", borderRadius: "var(--r-card)" }}
      p={3}
      mx={-3}
      borderRadius="var(--r-card)"
      transition="all 0.2s"
    >
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between" align="start">
          <RestaurantItemCard
            restaurant={restaurant}
            showRating={true}
            imageSize="80px"
          />
          <VStack spacing={1} align="end" minW="100px">
            <DistanceDisplay restaurant={restaurant} userLocation={userLocation} />
            <HStack spacing={1} color="var(--card-text-2)">
              <Clock size={12} />
              <Text fontSize="xs">
                {formatRatingDate(restaurant.ratedAt)}
              </Text>
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
};

const History = () => {
  const navigate = useNavigate();
  const router = useRouter();
  const userLocation = useSelector(selectPosition);
  const { data, error, isLoading } = useGetHistory();

  const handleBack = () => {
    router.history.back();
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <AppHeader page="history" />
        <Box minH="100vh">
          <Center h="calc(100vh - 120px)">
            <Box bg="var(--card-bg)" p={8} borderRadius="var(--r-card)" boxShadow="sm">
              <VStack spacing={4}>
                <Spinner size="lg" color="var(--card-text)" />
                <Text color="var(--card-text)">Loading your restaurant history...</Text>
              </VStack>
            </Box>
          </Center>
        </Box>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <AppHeader page="history" />
        <Box minH="100vh">
          <Center h="calc(100vh - 120px)">
            <Box bg="var(--card-bg)" p={8} borderRadius="var(--r-card)" boxShadow="sm">
              <VStack spacing={4} textAlign="center">
                <Text fontSize="lg" fontWeight="medium" color="var(--card-text)">
                  Unable to load your history
                </Text>
                <Text fontSize="sm" color="var(--card-text-2)">
                  Please check your internet connection and try again
                </Text>
                <Button
                  bg="black"
                  color="white"
                  onClick={() => window.location.reload()}
                  _hover={{ bg: "gray.800" }}
                >
                  Try Again
                </Button>
              </VStack>
            </Box>
          </Center>
        </Box>
      </PageWrapper>
    );
  }

  // Cast to HistoryRestaurant[] — the API client transforms snake_case→camelCase on response,
  // so ratedAt / distanceDisplay are present at runtime even though Restaurant[] doesn't declare them.
  const restaurants = (data?.restaurants || []) as HistoryRestaurant[];

  return (
    <PageWrapper>
      <SEO title="History" />
      <AppHeader page="history" />
      <Box minH="100vh">
        <Container maxW="4xl" py={6} px={{ base: 4, lg: 6 }}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="center">
              <Button
                leftIcon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={handleBack}
                size={{ base: 'sm', lg: 'md' }}
                color="white"
                _hover={{ bg: "gray.800" }}
              >
                Back
              </Button>
              <Badge bg="var(--card-bg)" color="var(--card-text)" px={3} py={1} borderRadius="full">
                {restaurants.length} restaurants
              </Badge>
            </HStack>

            <VStack spacing={2} align="center" textAlign="center">
              <HStack spacing={2} color="white">
                <Calendar size={24} />
                <Heading size={{ base: 'lg', lg: 'xl' }} color="white">Your Restaurant History</Heading>
              </HStack>
              <Text color="gray.400" fontSize={{ base: 'sm', lg: 'md' }}>
                Restaurants you&apos;ve been to, chronologically ordered
              </Text>
            </VStack>

            {/* Content */}
            {restaurants.length === 0 ? (
              <Center py={16}>
                <Box bg="var(--card-bg)" p={8} borderRadius="var(--r-card)" boxShadow="sm">
                  <VStack spacing={4} textAlign="center">
                    <Calendar size={48} color="var(--color-gray-300)" />
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="medium" color="var(--card-text)">
                        No restaurants in your history yet
                      </Text>
                      <Text fontSize="sm" color="var(--card-text-2)" maxW="300px">
                        Start rating restaurants to build your personal history and track your dining experiences
                      </Text>
                    </VStack>
                    <Button
                      bg="black"
                      color="white"
                      onClick={() => navigate({ to: '/search' })}
                      size="lg"
                      mt={4}
                      _hover={{ bg: "gray.800" }}
                    >
                      Find Restaurants
                    </Button>
                  </VStack>
                </Box>
              </Center>
            ) : (
              <VStack spacing={4} align="stretch">
                {restaurants.map((restaurant) => (
                  <Box key={restaurant.id} bg="var(--card-bg)" p={4} borderRadius="var(--r-card)" boxShadow="sm">
                    <HistoryItem restaurant={restaurant} userLocation={userLocation} />
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
};

export default History;